import { ICONS_TYPES } from '@/components/Icon';
import { getReportDownload } from '@/pages/tools/RevaluationTool/store/thunks';
import { BackgroundTask } from '@/pages/tools/RevaluationTool/types';
import { markTasksSeen } from '@/store/backgroundTasks/backgroundTasksSlice';
import {
  cancelAllBackgroundTasks,
  cancelBackgroundTask,
  pollBackgroundTasks,
} from '@/store/backgroundTasks/backgroundTasksThunks';
import {
  TASK_POLL_INTERVAL,
  isTaskActive,
  revalueTypeLabel,
} from '@/store/backgroundTasks/constants';
import {
  selectActiveBackgroundTaskCount,
  selectBackgroundTasks,
  selectBackgroundTasksPolledOnce,
  selectCancellingTaskIds,
  selectHasUnseenCompletedTasks,
} from '@/store/backgroundTasks/selectors';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Backdrop,
  Badge,
  BellButton,
  BellIcon,
  CancelAllButton,
  EmptyState,
  HeaderActions,
  PageButton,
  PageButtons,
  PageInfo,
  PaginationBar,
  Panel,
  PanelCount,
  PanelHeader,
  PanelTitle,
  ProgressFill,
  ProgressTrack,
  Root,
  RowCancelButton,
  RowDownloadButton,
  RowRight,
  StateText,
  StatusPill,
  TaskList,
  TaskMeta,
  TaskRow,
  TaskRowHeader,
  TypePill,
} from './styles';
import { BackgroundTasksPopoverProps, TaskTone } from './types';

const PAGE_SIZE = 4;

const taskTone = (task: BackgroundTask): TaskTone => {
  const status = (task.status ?? '').toLowerCase();
  if (task.statistic?.error || status === 'unknown') return 'error';
  if (status === 'finished') return 'done';
  if (status === 'running') return 'running';
  return 'queued';
};

const taskPercentage = (task: BackgroundTask): number => {
  if (task.total_count > 0) {
    return Math.round((task.current_count / task.total_count) * 100);
  }
  return 0;
};

const statusLabel = (task: BackgroundTask): string => {
  const status = (task.status ?? '').toLowerCase();
  if (status === 'unknown' || task.statistic?.error) return 'failed';
  return status || 'queued';
};

const BackgroundTasksPopover = ({ className }: BackgroundTasksPopoverProps) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const tasks = useAppSelector(selectBackgroundTasks);
  const activeCount = useAppSelector(selectActiveBackgroundTaskCount);
  const hasUnseen = useAppSelector(selectHasUnseenCompletedTasks);
  const polledOnce = useAppSelector(selectBackgroundTasksPolledOnce);
  const cancellingIds = useAppSelector(selectCancellingTaskIds);

  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);

  // Poll while authenticated so the bell stays in sync with runs the user
  // didn't start (or started then closed the modal).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    dispatch(pollBackgroundTasks());
    const intervalId = setInterval(() => {
      dispatch(pollBackgroundTasks());
    }, TASK_POLL_INTERVAL);
    return () => clearInterval(intervalId);
  }, [isAuthenticated, dispatch]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setPage(0);
    dispatch(markTasksSeen());
    dispatch(pollBackgroundTasks());
  }, [dispatch]);

  const close = useCallback(() => setOpen(false), []);

  const handleCancelAll = useCallback(() => {
    dispatch(cancelAllBackgroundTasks(undefined));
  }, [dispatch]);

  const handleCancelTask = useCallback(
    (taskId: string) => {
      dispatch(cancelBackgroundTask(taskId));
    },
    [dispatch],
  );

  const handleDownload = useCallback(
    (taskId: string) => {
      dispatch(getReportDownload(taskId));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Active runs first, then recently finished (backend already orders each
  // group most-recent-first).
  const sortedTasks = useMemo(
    () =>
      [...tasks].sort(
        (a, b) => Number(isTaskActive(b)) - Number(isTaskActive(a)),
      ),
    [tasks],
  );

  const pageCount = Math.max(1, Math.ceil(sortedTasks.length / PAGE_SIZE));
  // `page` can outrun the list when tasks roll off between polls — clamp on read.
  const safePage = Math.min(page, pageCount - 1);
  const pagedTasks = useMemo(
    () =>
      sortedTasks.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [sortedTasks, safePage],
  );

  const ringing = hasUnseen && !open;

  return (
    <Root className={className}>
      <BellButton open={open} onClick={open ? close : handleOpen}>
        <BellIcon
          icon={ringing ? ICONS_TYPES.BellRing : ICONS_TYPES.Bell}
          size={18}
          $ringing={ringing}
          open={open}
        />
        {activeCount > 0 && <Badge>{activeCount}</Badge>}
      </BellButton>

      {open && (
        <>
          <Backdrop onClick={close} />
          <Panel onClick={(e) => e.stopPropagation()}>
            <PanelHeader>
              <PanelTitle>Background tasks</PanelTitle>
              <HeaderActions>
                <PanelCount>
                  {activeCount > 0 ? `${activeCount} running` : 'Idle'}
                </PanelCount>
                {activeCount > 0 && (
                  <CancelAllButton onClick={handleCancelAll}>
                    Cancel all
                  </CancelAllButton>
                )}
              </HeaderActions>
            </PanelHeader>

            {sortedTasks.length === 0 ? (
              <EmptyState>
                {polledOnce
                  ? 'No background tasks yet. Start a revaluation and track its progress here.'
                  : 'Loading…'}
              </EmptyState>
            ) : (
              <TaskList>
                {pagedTasks.map((task) => {
                  const cancelling = cancellingIds.includes(task.task_id);
                  const tone: TaskTone = cancelling ? 'queued' : taskTone(task);
                  const pct = tone === 'done' ? 100 : taskPercentage(task);
                  // Already-cancelling runs can't be cancelled again; only true
                  // active (non-cancelling) runs offer the Cancel action.
                  const active = isTaskActive(task) && !cancelling;
                  return (
                    <TaskRow key={task.task_id}>
                      <TaskRowHeader>
                        <TypePill>
                          {revalueTypeLabel(task.revalue_type)}
                        </TypePill>
                        <RowRight>
                          <StatusPill $tone={tone}>
                            {cancelling ? 'cancelling' : statusLabel(task)}
                          </StatusPill>
                          {active && (
                            <RowCancelButton
                              onClick={() => handleCancelTask(task.task_id)}>
                              Cancel
                            </RowCancelButton>
                          )}
                          {!cancelling && tone === 'done' && (
                            <RowDownloadButton
                              onClick={() => handleDownload(task.task_id)}>
                              Download
                            </RowDownloadButton>
                          )}
                        </RowRight>
                      </TaskRowHeader>

                      {task.state && <StateText>{task.state}</StateText>}

                      <ProgressTrack>
                        <ProgressFill $value={pct} $tone={tone} />
                      </ProgressTrack>

                      <TaskMeta>
                        <span>
                          {task.current_count} / {task.total_count} ({pct}%)
                        </span>
                        {task.statistic?.duration && (
                          <span>{task.statistic.duration}</span>
                        )}
                      </TaskMeta>
                    </TaskRow>
                  );
                })}
              </TaskList>
            )}

            {pageCount > 1 && (
              <PaginationBar>
                <PageInfo>
                  Page {safePage + 1} of {pageCount}
                </PageInfo>
                <PageButtons>
                  <PageButton
                    onClick={() => setPage(Math.max(0, safePage - 1))}
                    disabled={safePage === 0}
                    aria-label="Previous page">
                    ‹
                  </PageButton>
                  <PageButton
                    onClick={() =>
                      setPage(Math.min(pageCount - 1, safePage + 1))
                    }
                    disabled={safePage >= pageCount - 1}
                    aria-label="Next page">
                    ›
                  </PageButton>
                </PageButtons>
              </PaginationBar>
            )}
          </Panel>
        </>
      )}
    </Root>
  );
};

export default BackgroundTasksPopover;
